import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { withEmailDeliveryNote } from "@/components/EmailDeliveryNotice";
import {
  Activity, AlertTriangle, BarChart3, CheckCircle2, Clock3, Eye, LogIn,
  Mail, MousePointerClick, Pause, Play, RefreshCw, Search, Send, UserCheck, X,
} from "@/lib/icons";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const EMAIL_TYPES = [
  { value: "welcome", label: "Welcome / account confirmation" },
  { value: "profile_nudge", label: "Complete your profile" },
  { value: "features", label: "Invoice and ID card spotlight" },
  { value: "profile_complete", label: "Profile completed" },
  { value: "card_redesign", label: "New ID card design" },
];

const compact = (value) => new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 }).format(value || 0);
const dateText = (value) => value ? new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "-";
const pct = (value, total) => total ? `${Math.round((value / total) * 100)}%` : "0%";

function StatusDot({ active, label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] ${active ? "text-emerald-300" : "text-white/30"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-400" : "bg-white/20"}`} /> {label}
    </span>
  );
}

function ConfirmModal({ details, onCancel, onConfirm, busy }) {
  if (!details) return null;
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/70 p-4 backdrop-blur-sm" data-testid="email-confirm-modal">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#171720] p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-fuchsia-400/25 bg-fuchsia-500/15 text-fuchsia-200"><Send size={17} /></span>
          <div>
            <h2 className="font-display text-2xl text-white">Confirm email send</h2>
            <p className="mt-1 text-sm leading-6 text-white/55">You are about to send <span className="font-semibold text-white">{details.label}</span> to <span className="font-semibold text-white">{details.count} member{details.count === 1 ? "" : "s"}</span>.</p>
          </div>
        </div>
        <div className="mt-5 rounded-xl border border-amber-400/20 bg-amber-500/10 p-3 text-xs leading-5 text-amber-100/80">This can send real email immediately. Members without an email address will be skipped.</div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="btn-ghost rounded-full px-5 py-2 text-sm text-white">Cancel</button>
          <button onClick={onConfirm} disabled={busy} data-testid="email-confirm-send" className="btn-glow rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? "Sending…" : "Confirm and send"}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminEmail() {
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectionHandled = useRef(false);
  const [mode, setMode] = useState("onboarding");
  const [smtp, setSmtp] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [days, setDays] = useState("30");
  const [q, setQ] = useState("");
  const [memberStatus, setMemberStatus] = useState("all");
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Map());
  const [allMatching, setAllMatching] = useState(false);
  const [emailType, setEmailType] = useState("welcome");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [history, setHistory] = useState(null);
  const [configBusy, setConfigBusy] = useState(false);

  const loadAnalytics = useCallback(() => {
    api.get("/admin/onboarding/analytics", { params: { days } })
      .then((r) => setAnalytics(r.data))
      .catch((e) => toast.error(formatError(e)));
  }, [days]);

  useEffect(() => {
    api.get("/admin/email/status").then((r) => setSmtp(r.data)).catch(() => setSmtp({ configured: false }));
  }, []);
  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  useEffect(() => {
    if (preselectionHandled.current) return;
    const ids = (searchParams.get("members") || "").split(",").map((value) => value.trim()).filter(Boolean).slice(0, 300);
    if (!ids.length) return;
    preselectionHandled.current = true;
    if (searchParams.get("mode") === "broadcast") setMode("broadcast");
    Promise.all(ids.map((memberId) => api.get(`/admin/members/${encodeURIComponent(memberId)}`).then((response) => response.data).catch(() => null)))
      .then((members) => {
        const found = members.filter(Boolean);
        setSelected(new Map(found.map((member) => [member.member_id, { full_name: member.full_name, email: member.email }])));
        setAllMatching(false);
        if (found.length) toast.success(`${found.length} member${found.length === 1 ? "" : "s"} selected for email`);
        if (found.length !== ids.length) toast.error(`${ids.length - found.length} member${ids.length - found.length === 1 ? " was" : "s were"} not available`);
        setSearchParams({}, { replace: true });
      });
  }, [searchParams, setSearchParams]);

  const loadMembers = useCallback(() => {
    setLoading(true);
    api.get("/admin/members", { params: { q, status: memberStatus, page, limit: 25 } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [q, memberStatus, page]);
  useEffect(() => { loadMembers(); }, [loadMembers]);

  const resetSelection = () => { setSelected(new Map()); setAllMatching(false); };
  const changeSearch = (value) => { setQ(value); setPage(1); setAllMatching(false); };
  const changeStatus = (value) => { setMemberStatus(value); setPage(1); setAllMatching(false); };
  const toggleOne = (member) => {
    setAllMatching(false);
    setSelected((previous) => {
      const next = new Map(previous);
      if (next.has(member.member_id)) next.delete(member.member_id);
      else next.set(member.member_id, { full_name: member.full_name, email: member.email });
      return next;
    });
  };
  const allOnPageSelected = data.items.length > 0 && data.items.every((member) => selected.has(member.member_id));
  const toggleAllOnPage = () => {
    setAllMatching(false);
    setSelected((previous) => {
      const next = new Map(previous);
      data.items.forEach((member) => {
        if (allOnPageSelected) next.delete(member.member_id);
        else next.set(member.member_id, { full_name: member.full_name, email: member.email });
      });
      return next;
    });
  };

  const selectedList = useMemo(() => Array.from(selected.entries()).map(([member_id, value]) => ({ member_id, ...value })), [selected]);
  const recipientCount = allMatching ? data.total : selectedList.length;
  const selectedWithoutEmail = selectedList.filter((member) => !member.email).length;

  const requestSend = () => {
    if (!recipientCount) return toast.error("Select at least one recipient");
    if (mode === "broadcast" && (!subject.trim() || !message.trim())) return toast.error("Subject and message are required");
    const label = mode === "onboarding"
      ? EMAIL_TYPES.find((item) => item.value === emailType)?.label
      : subject.trim();
    setConfirm({ label, count: recipientCount });
  };

  const performSend = async () => {
    setSending(true);
    try {
      let result;
      if (mode === "onboarding") {
        result = (await api.post("/admin/onboarding/resend", {
          member_ids: selectedList.map((member) => member.member_id),
          select_all: allMatching,
          q,
          status: memberStatus,
          email_type: emailType,
        })).data;
      } else {
        result = (await api.post("/admin/email/bulk", {
          member_ids: selectedList.map((member) => member.member_id), subject: subject.trim(), message,
        })).data;
      }
      toast.success(withEmailDeliveryNote(`Sent ${result.sent || 0} email${result.sent === 1 ? "" : "s"}${result.failed ? ` · ${result.failed} failed` : ""}${result.skipped?.length ? ` · ${result.skipped.length} skipped` : ""}.`));
      resetSelection();
      if (mode === "broadcast") { setSubject(""); setMessage(""); }
      setConfirm(null);
      loadAnalytics();
    } catch (error) { toast.error(formatError(error)); }
    finally { setSending(false); }
  };

  const togglePaused = async () => {
    setConfigBusy(true);
    try {
      const paused = !analytics?.paused;
      await api.put("/admin/onboarding/config", { paused });
      setAnalytics((current) => ({ ...current, paused }));
      toast.success(paused ? "Automatic onboarding paused" : "Automatic onboarding resumed");
    } catch (error) { toast.error(formatError(error)); }
    finally { setConfigBusy(false); }
  };

  const openHistory = async (summary) => {
    try {
      const { data: response } = await api.get("/admin/onboarding/events", { params: { member_id: summary.member_id } });
      setHistory({ member: summary, items: response.items });
    } catch (error) { toast.error(formatError(error)); }
  };

  const summary = analytics?.summary || {};
  const cards = [
    { label: "SMTP accepted", value: summary.sent, icon: Send, tone: "text-violet-300", sub: `${summary.unique_recipients || 0} members` },
    { label: "Opened", value: summary.opened, icon: Eye, tone: "text-sky-300", sub: pct(summary.opened, summary.sent) },
    { label: "Clicked", value: summary.clicked, icon: MousePointerClick, tone: "text-fuchsia-300", sub: pct(summary.clicked, summary.sent) },
    { label: "Logged in", value: summary.logged_in, icon: LogIn, tone: "text-amber-300", sub: pct(summary.logged_in, summary.sent) },
    { label: "Profile completed", value: summary.profile_completed, icon: UserCheck, tone: "text-emerald-300", sub: pct(summary.profile_completed, summary.sent) },
    { label: "Failed", value: summary.failed, icon: AlertTriangle, tone: "text-red-300", sub: pct(summary.failed, (summary.sent || 0) + (summary.failed || 0)) },
  ];

  return (
    <div className="space-y-6">
      <ConfirmModal details={confirm} onCancel={() => setConfirm(null)} onConfirm={performSend} busy={sending} />

      <div className="admin-page-heading fade-up">
        <div>
          <div className="admin-page-kicker">Member communication</div>
          <h1 className="mt-2 font-display text-4xl text-white sm:text-5xl">Email &amp; Onboarding</h1>
          <p className="admin-page-description">Manage automated journeys, send targeted messages and understand how members respond.</p>
        </div>
        <button onClick={togglePaused} disabled={configBusy || !analytics} data-testid="onboarding-pause-toggle"
          className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold ${analytics?.paused ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200" : "border-amber-400/30 bg-amber-500/10 text-amber-100"}`}>
          {analytics?.paused ? <Play size={14} /> : <Pause size={14} />}
          {analytics?.paused ? "Resume automatic sequence" : "Pause automatic sequence"}
        </button>
      </div>

      <div className={`rounded-2xl border p-4 ${analytics?.paused ? "border-amber-400/25 bg-amber-500/10" : "border-emerald-400/20 bg-emerald-500/10"}`}>
        <div className="flex items-center gap-2 text-sm font-semibold text-white">
          {analytics?.paused ? <Pause size={15} className="text-amber-300" /> : <Activity size={15} className="text-emerald-300" />}
          Automatic onboarding is {analytics?.paused ? "paused" : "running"}
        </div>
        <p className="mt-1 text-xs text-white/50">Manual admin sends remain available. Analytics records are automatically deleted after {analytics?.retention_days || 365} days.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        {cards.map((card) => (
          <div key={card.label} className="glass rounded-2xl p-4">
            <div className={`flex items-center gap-2 text-xs ${card.tone}`}><card.icon size={13} /> {card.label}</div>
            <div className="mt-3 font-display text-3xl text-white">{compact(card.value)}</div>
            <div className="mt-1 text-[11px] text-white/40">{card.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.35fr_.65fr]">
        <div className="glass rounded-2xl p-5">
          <div className="flex items-center justify-between gap-3">
            <div><div className="flex items-center gap-2 font-display text-xl text-white"><BarChart3 size={17} className="text-fuchsia-300" /> Email funnel</div><div className="mt-1 text-xs text-white/40">Opens are approximate; clicks and logins are first-party signals.</div></div>
            <Select value={days} onValueChange={setDays}><SelectTrigger className="w-28 border-white/10 bg-white/5 text-white"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="30">30 days</SelectItem><SelectItem value="90">90 days</SelectItem><SelectItem value="365">1 year</SelectItem></SelectContent></Select>
          </div>
          <div className="mt-5 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analytics?.daily || []} margin={{ left: -20, right: 8 }}>
                <defs><linearGradient id="emailSent" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#9D00FF" stopOpacity={0.45} /><stop offset="100%" stopColor="#9D00FF" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid stroke="rgba(255,255,255,.06)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#8f8a99", fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={28} />
                <YAxis allowDecimals={false} tick={{ fill: "#8f8a99", fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#171720", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12 }} />
                <Area type="monotone" dataKey="sent" stroke="#9D00FF" fill="url(#emailSent)" strokeWidth={2} />
                <Area type="monotone" dataKey="opened" stroke="#38bdf8" fill="none" strokeWidth={2} />
                <Area type="monotone" dataKey="clicked" stroke="#f0abfc" fill="none" strokeWidth={2} />
                <Area type="monotone" dataKey="logged_in" stroke="#fbbf24" fill="none" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass rounded-2xl p-5" data-testid="admin-email-status">
          <div className="font-display text-xl text-white">System status</div>
          {smtp == null ? <div className="mt-5 text-sm text-white/40">Checking SMTP…</div> : smtp.configured ? (
            <div className="mt-5 flex items-start gap-3"><CheckCircle2 size={18} className="mt-0.5 text-emerald-300" /><div><div className="text-sm font-semibold text-white">Hostinger SMTP configured</div><div className="mt-1 break-all text-xs leading-5 text-white/45">{smtp.from_name} &lt;{smtp.from_email}&gt;<br />{smtp.host}:{smtp.port}</div></div></div>
          ) : (
            <div className="mt-5 flex items-start gap-3"><AlertTriangle size={18} className="mt-0.5 text-amber-300" /><div><div className="text-sm font-semibold text-white">SMTP not configured</div><div className="mt-1 text-xs leading-5 text-white/45">Set SMTP_USER and SMTP_PASSWORD on the VPS.</div></div></div>
          )}
          <div className="mt-5 rounded-xl border border-white/10 bg-white/[.03] p-3 text-xs leading-5 text-white/45">“Sent” means SMTP accepted the message. Hostinger SMTP does not provide delivery or bounce webhooks, so inbox delivery cannot be guaranteed here.</div>
        </div>
      </div>

      <div className="flex gap-2 rounded-xl border border-white/10 bg-white/[.03] p-1 w-fit">
        <button onClick={() => setMode("onboarding")} className={`rounded-lg px-4 py-2 text-sm ${mode === "onboarding" ? "bg-violet-600 text-white" : "text-white/50"}`}>Onboarding templates</button>
        <button onClick={() => { setMode("broadcast"); setAllMatching(false); }} className={`rounded-lg px-4 py-2 text-sm ${mode === "broadcast" ? "bg-violet-600 text-white" : "text-white/50"}`}>Custom broadcast</button>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-2">
        <div className="glass rounded-2xl p-5 min-w-0">
          <div className="flex items-center justify-between"><div className="font-display text-xl text-white">Recipients</div>{recipientCount > 0 && <button onClick={resetSelection} className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-white"><X size={12} /> Clear ({recipientCount})</button>}</div>
          <div className="mt-4 flex flex-wrap gap-2">
            <div className="relative min-w-[170px] flex-1"><Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" /><Input className="h-9 border-white/10 bg-white/5 pl-8 text-sm text-white" placeholder="Search members…" value={q} onChange={(event) => changeSearch(event.target.value)} /></div>
            <Select value={memberStatus} onValueChange={changeStatus}><SelectTrigger className="h-9 w-36 border-white/10 bg-white/5 text-sm text-white"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem><SelectItem value="blocked">Blocked</SelectItem></SelectContent></Select>
          </div>
          {mode === "onboarding" && data.total > 0 && (
            <button onClick={() => { setAllMatching(true); setSelected(new Map()); }} data-testid="email-select-all-matching" className={`mt-3 w-full rounded-xl border px-3 py-2 text-xs ${allMatching ? "border-fuchsia-400/30 bg-fuchsia-500/15 text-fuchsia-100" : "border-white/10 bg-white/[.03] text-white/55 hover:text-white"}`}>{allMatching ? `All ${data.total} matching members selected` : `Select all ${data.total} matching members`}</button>
          )}
          <div className="mt-3 overflow-hidden rounded-xl border border-white/10">
            <label className="flex items-center gap-2 bg-white/5 px-3 py-2 text-xs text-white/50"><input type="checkbox" checked={!allMatching && allOnPageSelected} onChange={toggleAllOnPage} className="accent-fuchsia-500" /> Select all on this page</label>
            <div className="max-h-[360px] divide-y divide-white/5 overflow-y-auto">
              {loading && <div className="px-3 py-6 text-center text-sm text-white/40">Loading…</div>}
              {!loading && data.items.map((member) => (
                <label key={member.member_id} className="flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-white/5">
                  <input type="checkbox" checked={allMatching || selected.has(member.member_id)} onChange={() => toggleOne(member)} className="accent-fuchsia-500" />
                  <div className="min-w-0 flex-1"><div className="truncate text-sm text-white">{member.full_name}</div><div className="truncate text-[11px] text-white/40">{member.email || "No email on file"}</div></div>
                  <span className="text-[10px] uppercase tracking-wider text-white/30">{member.status}</span>
                </label>
              ))}
              {!loading && !data.items.length && <div className="px-3 py-6 text-center text-sm text-white/40">No members found.</div>}
            </div>
          </div>
          {data.total > 25 && <div className="mt-3 flex items-center justify-between text-xs text-white/45"><span>Page {page} · {data.total} total</span><div className="flex gap-2"><button disabled={page === 1} onClick={() => setPage((value) => value - 1)} className="btn-ghost rounded-full px-3 py-1 disabled:opacity-30">Prev</button><button disabled={page * 25 >= data.total} onClick={() => setPage((value) => value + 1)} className="btn-ghost rounded-full px-3 py-1 disabled:opacity-30">Next</button></div></div>}
        </div>

        <div className="glass rounded-2xl p-5 min-w-0">
          <div className="font-display text-xl text-white">{mode === "onboarding" ? "Send onboarding email" : "Compose broadcast"}</div>
          <div className="mt-1 text-xs text-white/50">{recipientCount ? `${recipientCount} recipient${recipientCount === 1 ? "" : "s"} selected${selectedWithoutEmail ? ` · ${selectedWithoutEmail} without email` : ""}` : "Select recipients on the left."}</div>
          {mode === "onboarding" ? (
            <div className="mt-5"><label className="overline text-[10px] text-white/50">Template</label><Select value={emailType} onValueChange={setEmailType}><SelectTrigger className="mt-1.5 border-white/10 bg-white/5 text-white"><SelectValue /></SelectTrigger><SelectContent>{EMAIL_TYPES.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectContent></Select><div className="mt-4 rounded-xl border border-white/10 bg-white/[.03] p-4 text-xs leading-5 text-white/50">This is a manual resend and does not reset the member’s automatic sequence schedule. Opens, clicks, login, and profile completion will be recorded for one year.</div></div>
          ) : (
            <div className="mt-4 space-y-3"><div><label className="overline text-[10px] text-white/50">Subject</label><Input className="mt-1.5 border-white/10 bg-white/5 text-white" value={subject} onChange={(event) => setSubject(event.target.value)} /></div><div><label className="overline text-[10px] text-white/50">Message</label><Textarea className="mt-1.5 min-h-[210px] border-white/10 bg-white/5 text-sm text-white" value={message} onChange={(event) => setMessage(event.target.value)} /></div></div>
          )}
          <button onClick={requestSend} disabled={sending || !smtp?.configured || !recipientCount} className="btn-glow mt-5 inline-flex h-11 w-full items-center justify-center gap-2 rounded-full font-semibold text-white disabled:opacity-50"><Send size={14} /> Review and send</button>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[.8fr_1.2fr]">
        <div className="glass rounded-2xl p-5 min-w-0">
          <div className="flex items-center justify-between"><div className="font-display text-xl text-white">Member email history</div><RefreshCw size={14} className="text-white/30" /></div>
          <div className="mt-4 max-h-[420px] space-y-2 overflow-y-auto pr-1">
            {(analytics?.member_summaries || []).map((member) => (
              <button key={member.member_id} onClick={() => openHistory(member)} className="w-full rounded-xl border border-white/8 bg-white/[.025] p-3 text-left hover:border-fuchsia-400/25">
                <div className="flex items-center justify-between gap-2"><div className="min-w-0"><div className="truncate text-sm font-medium text-white">{member.member_name || member.member_id}</div><div className="truncate text-[11px] text-white/35">{member.recipient}</div></div><span className="text-xs text-fuchsia-200">View</span></div>
                <div className="mt-2 flex flex-wrap gap-2"><StatusDot active={member.opened} label={`${member.opened} open`} /><StatusDot active={member.clicked} label={`${member.clicked} click`} /><StatusDot active={member.logged_in} label={`${member.logged_in} login`} /><StatusDot active={member.completed} label={`${member.completed} complete`} /></div>
              </button>
            ))}
            {!analytics?.member_summaries?.length && <div className="py-10 text-center text-sm text-white/35">Tracked member activity will appear here.</div>}
          </div>
        </div>

        <div className="glass rounded-2xl p-5 min-w-0">
          <div className="flex items-center justify-between gap-3"><div><div className="font-display text-xl text-white">{history ? `${history.member.member_name || history.member.member_id}: history` : "Recent email activity"}</div><div className="mt-1 text-xs text-white/40">Events created before tracking was enabled are not recoverable.</div></div>{history && <button onClick={() => setHistory(null)} className="btn-ghost rounded-full px-3 py-1 text-xs text-white">Show all</button>}</div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-xs">
              <thead className="text-white/35"><tr className="border-b border-white/10"><th className="py-2 pr-3 font-medium">Member</th><th className="py-2 pr-3 font-medium">Email</th><th className="py-2 pr-3 font-medium">Status</th><th className="py-2 pr-3 font-medium">Engagement</th><th className="py-2 font-medium">Sent</th></tr></thead>
              <tbody className="divide-y divide-white/5">
                {(history?.items || analytics?.recent_events || []).map((event) => (
                  <tr key={event.event_id}><td className="py-3 pr-3"><div className="text-white">{event.member_name || event.member_id}</div><div className="text-[10px] text-white/30">{event.member_id}</div></td><td className="py-3 pr-3"><div className="text-white/70">{event.email_label}</div><div className="text-[10px] text-white/30">{event.source === "manual_admin" ? "Manual resend" : "Automatic"}</div></td><td className="py-3 pr-3"><span className={event.status === "sent" ? "text-emerald-300" : event.status === "failed" ? "text-red-300" : "text-amber-300"}>{event.status}</span></td><td className="py-3 pr-3"><div className="flex flex-wrap gap-2"><StatusDot active={event.opened_at} label="Opened" /><StatusDot active={event.clicked_at} label="Clicked" /><StatusDot active={event.login_at} label="Login" /><StatusDot active={event.profile_completed_at} label="Complete" /></div></td><td className="py-3 text-white/40">{dateText(event.sent_at || event.created_at)}</td></tr>
                ))}
              </tbody>
            </table>
            {!(history?.items || analytics?.recent_events || []).length && <div className="py-10 text-center text-sm text-white/35">No tracked email events yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
